import React, { LegacyRef, PropsWithChildren, useEffect, useMemo, useRef, useState } from 'react'
import { DropDownListBlurEvent, DropDownListChangeEvent, DropDownListHandle, ListItemProps } from "@progress/kendo-react-dropdowns";
import DropdownOption from './DropdownOption';
import SearchableDropdown from './SearchableDropdown';
import { UserService } from '../../services';
import { UserModel } from '../../data/entities';
import { createSyntheticEvent } from '../../utils';

interface SearchableUserDropdownProps extends PropsWithChildren {
  value?: any;
  name: string;
  isInvalid?: boolean;
  onBlur?: (e: DropDownListBlurEvent) => void;
  onChange?: (e: DropDownListChangeEvent) => void;
  onSelect?: (value: DropdownOption) => void;
  placeholder?: string;
  users?: DropdownOption[];
  defaultItem?: boolean;
  filterUsers?: number[];
  disabled?: boolean;
}

interface UserDropdownOption extends DropdownOption {
  email?: string; 
  displayName?: string;
}

export const SearchableUserDropdown = React.forwardRef(
  (
    props: Readonly<SearchableUserDropdownProps>,
    ref: LegacyRef<DropDownListHandle> | undefined
  ) => {
    const dropdownRef = useRef(null);
    const userService = useMemo(() => new UserService(), []);
    const [options, setOptions] = useState<DropdownOption[]>([]);

    useEffect(() => {
      if (props.users) {
        setOptions(props.users.sort((a, b) => a.text.localeCompare(b.text)));
        let activeRef = (ref ?? dropdownRef as any)?.current as DropDownListHandle;
        let select = activeRef?.element?.querySelector('select');
        if (select) {
          const event = new Event('select', { bubbles: true });
          Object.defineProperty(event, 'target', { writable: false, value: select });
          const syntheticEvent = createSyntheticEvent(event) as React.ChangeEvent<typeof select>;
          props.onChange?.call(this, {
            nativeEvent: event,
            syntheticEvent: syntheticEvent,
            target: activeRef,
            value: activeRef.props.value
          });
        }
      } 
      else {
        const fetchData = async () => {
          let users = await userService.getAll();
          if (props.filterUsers && props.filterUsers.length >= 0) {
            users = users.filter((user) => props.filterUsers?.includes(user.id ?? -1)); 
          }
            const newOptions: UserDropdownOption[] = users
            .map<UserDropdownOption>((user: UserModel) => ({
              value: user.id?.toString() ?? "",
              text: `${user.displayName}`,
              email: user.email,
            }))
            .sort((a, b) => a.text.localeCompare(b.text));
          setOptions(newOptions);
        };
        fetchData();
      }
    }, [userService, props.users, props.value]);

    const getValue = () => {   
      if (props.value) {
        return options.find((option) => option.value === props.value?.toString()) ;
      }
    };
    const itemRender = (li: React.ReactElement<HTMLLIElement>, itemProps: ListItemProps) => {
      const item = itemProps.dataItem as UserDropdownOption;
    
      return React.cloneElement(li, li.props,
        <div className="d-flex flex-column">
          <span className="fw-semibold">{item.displayName ?? item.text}</span>
          {item.email && <small className="text-muted">{item.email}</small>}
        </div>
      );
    };    

    return (
      <SearchableDropdown
        defaultItem={props.defaultItem ? {text:"Select a User", value:"-1"} : undefined}
        ref={ref ?? dropdownRef}
        label="User"
        name={props.name}
        itemRender={itemRender}
        options={options}
        value={getValue()}
        onChange={props.onChange}
        onSelect={props.onSelect}
        isInvalid={props.isInvalid}
        disabled={props.disabled}
      >
        {props.children}
      </SearchableDropdown>
    );
  }
);

export default SearchableUserDropdown
